import { Skeleton, IconButton } from "@mui/material";
import { useEffect, Fragment, useState, useMemo } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useParams } from "react-router";
import { useSnapshot } from "valtio";

import viewerState, {
  updateSegmentationVisibility,
  isNiftiMetadata,
} from "stores/viewer";
import useMarkers from "shared/hooks/useMarkers";
import SequenceMarkers from "./SequenceMarkers";
import { switchNiftiBase } from "lib/cornerstone/setupCornerstoneViewer";
import { bgDepth, shadowDepth } from "shared/utils/color";

import { petMaskId, togglePetMask } from "lib/cornerstone/petViewer";
import {
  petTracerLabel,
  isPetModality,
} from "lib/cornerstone/helpers/niftiDiscovery";

function getPetValues(maskUrl: string, components: any[]) {
  const match = maskUrl.match(/_segm-([^/]+)$/);
  if (!match) return { suv: null, suvr: null, centiloid: null };
  const variant = match[1];

  const targetTerms = variant.split("-");
  const isLeft = targetTerms.includes("left");
  const isRight = targetTerms.includes("right");
  const base = variant
    .replace("-left", "")
    .replace("-right", "")
    .replace(/-$/, "");

  let mappedBase = base;
  if (base === "cerebellum-cortex") mappedBase = "cerebellar cortex";
  else if (base === "anterior-cingulate-cortex")
    mappedBase = "anterior cingulate gyrus";
  else if (base === "posterior-cingulate-cortex")
    mappedBase = "posterior cingulate gyrus";
  else if (base === "parietal-cortex") mappedBase = "parietal lobe";
  else if (base === "insular-cortex") mappedBase = "insula";
  else if (base === "medial-frontal-cortex")
    mappedBase = "medial frontal gyrus";
  else if (base === "grey-matter-lobes") mappedBase = "cerebral cortex";
  else if (base === "template-cortex")
    mappedBase = "region of gray matter of neuraxis";
  else mappedBase = base.replace(/-/g, " ");

  let suv: number | null = null;
  let suvr: number | null = null;
  let centiloid: number | null = null;

  for (const comp of components) {
    const text = (comp.text || "").toLowerCase();
    if (!text) continue;

    const isSUV =
      text.includes("mean uptake value") ||
      comp.coding?.[0]?.code === "RID39224";
    const isSUVR =
      text.includes("standardized uptake value ratio") ||
      comp.coding?.[0]?.code === "RID12782";
    const isCentiloid =
      text.includes("centiloid") || comp.coding?.[0]?.code === "RID12790";

    if (!isSUV && !isSUVR && !isCentiloid) continue;

    const parts = text.split(",");
    if (parts.length < 3) continue;

    const regionPart = parts[2].split("/")[0].trim();

    let matchSide = true;
    if (isLeft && !regionPart.includes("left")) matchSide = false;
    if (isRight && !regionPart.includes("right")) matchSide = false;
    if (
      !isLeft &&
      !isRight &&
      (regionPart.includes("left") || regionPart.includes("right"))
    )
      matchSide = false;

    let matchBase = false;
    if (mappedBase === "cerebral cortex") {
      if (isLeft && regionPart.includes("left cerebral hemisphere"))
        matchBase = true;
      else if (isRight && regionPart.includes("right cerebral hemisphere"))
        matchBase = true;
      else if (!isLeft && !isRight && regionPart === "cerebral cortex")
        matchBase = true;
    } else {
      const baseWords = mappedBase.split(" ");
      matchBase = baseWords.every((w) => regionPart.includes(w));
    }
    console.log("matchside: ", matchSide);
    console.log("matchbase: ", mappedBase);

    if (matchSide && matchBase) {
      const val = comp.valueQuantity?.value;
      if (typeof val === "number") {
        if (isSUV && suv === null) suv = val;
        if (isSUVR && suvr === null) suvr = val;
        if (isCentiloid && centiloid === null) centiloid = val;
      }
    }
  }

  return { suv, suvr, centiloid };
}

function PetMaskRow({
  mask,
  selected,
  values,
}: {
  mask: any;
  selected: boolean;
  values: any;
}) {
  const [busy, setBusy] = useState(false);

  const handleToggle = async () => {
    setBusy(true);
    try {
      await togglePetMask({ ...mask, color: [...mask.color] }, !selected);
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className={`shadow-inner ${shadowDepth[0]} ${bgDepth[0]}`}>
      <td className="py-2 text-center">
        <IconButton size="small" disabled={busy} onClick={handleToggle}>
          {selected ? (
            <VisibilityIcon style={{ color: `rgb(${mask.color.join(",")})` }} />
          ) : (
            <VisibilityOffIcon className={busy ? "opacity-50" : ""} />
          )}
        </IconButton>
      </td>
      <td className="py-2 text-left">
        <span className="inline-block first-letter:uppercase">
          {mask.label}
        </span>
      </td>
      <td className="py-2">
        {values.suv !== null ? values.suv.toFixed(3) : "-"}
      </td>
      <td className="py-2">
        {values.suvr !== null ? values.suvr.toFixed(3) : "-"}
      </td>
      <td className="py-2">
        {values.centiloid !== null ? values.centiloid.toFixed(3) : "-"}
      </td>
      <td className="py-2 flex justify-end"></td>
    </tr>
  );
}

export default function QuantificationTable() {
  const { study_id } = useParams();
  const {
    naturalizedDatasets,
    displaySegment,
    isLoading,
    populatedMarkers,
    observationData,
  } = useMarkers(study_id);

  const snap = useSnapshot(viewerState);
  const {
    niftiDatasets,
    niftiActiveModality,
    niftiPetMasks,
    segmentsPerSeries,
    instanceMetadata,
  } = snap;

  const observationComponents = useMemo(
    () => observationData?.component || [],
    [observationData],
  );

  useEffect(() => {
    if (
      niftiActiveModality &&
      niftiActiveModality !== "t1mri" &&
      !isPetModality(niftiActiveModality)
    ) {
      viewerState.segmentsPerSeries = viewerState.segmentsPerSeries.map(
        (s) => ({ ...s, selected: false }),
      );
      updateSegmentationVisibility().catch(console.error);
      const t1Url = (viewerState.niftiBases as any)?.t1mri;
      if (t1Url) {
        switchNiftiBase(t1Url).catch(console.error);
        viewerState.niftiActiveModality = "t1mri";
        viewerState.focusedMarkerCode = null;
      }
    }
  }, []);

  if (isLoading) {
    return (
      <div>
        <Skeleton
          className="bg-stone-500 leading-0"
          animation={false}
          height={50}
        />
        <Skeleton className="bg-stone-500" animation="wave" height={50} />
        <Skeleton className="bg-stone-500" animation="wave" height={50} />
        <Skeleton className="bg-stone-500" animation="wave" height={50} />
      </div>
    );
  }

  const isNiftiMode = isNiftiMetadata(instanceMetadata);
  const dicomSegDatasets = isNiftiMode
    ? []
    : naturalizedDatasets?.filter((d) => d.Modality === "SEG") ?? [];
  const allSegDatasets = [...dicomSegDatasets, ...niftiDatasets].filter(
    (d) => d.SeriesInstanceUID !== "nifti:seg:t2starmri",
  );

  const currentPetMasks = niftiPetMasks.filter(
    (m) => m.modality === niftiActiveModality,
  );
  const showPetSection =
    isPetModality(niftiActiveModality) && currentPetMasks.length > 0;

  return (
    <>
      <table
        data-testid="viewer-mode-quantification"
        className="w-full rounded overflow-x-clip text-right"
      >
        <thead className="bg-stone-600 rounded sticky top-0 z-50 ">
          <tr>
            <th className="py-2 rounded-tl w-8"></th>
            <th className="py-2 text-left w-72">Structure</th>
            {showPetSection ? (
              <>
                <th className="py-2">SUV</th>
                <th className="py-2">SUVR</th>
                <th className="py-2">Centiloid</th>
              </>
            ) : (
              <>
                <th className="py-2">Volume(mL)</th>
                <th className="py-2">Count</th>
                <th className="py-2">Volume(%ICV)</th>
                <th className="py-2">Zscore</th>
                <th className="py-2">Percentile</th>
              </>
            )}
            <th className="py-2 rounded-tr w-8"></th>
          </tr>
        </thead>

        <tbody>
          {!showPetSection &&
            allSegDatasets.length > 0 &&
            allSegDatasets
              .sort((dA, dB) => {
                const orderA = (dA as any)._order ?? Infinity;
                const orderB = (dB as any)._order ?? Infinity;
                return orderA !== Infinity || orderB !== Infinity
                  ? orderA - orderB
                  : (dA.SeriesDescription ?? "").localeCompare(
                      dB.SeriesDescription ?? "",
                    );
              })
              .map((d) => (
                <SequenceMarkers
                  key={d.SeriesInstanceUID}
                  dataset={d}
                  populatedMarkers={populatedMarkers}
                  displaySegment={displaySegment}
                />
              ))}

          {showPetSection &&
            (["reference", "interest", "other"] as const).map((role) => {
              const group = currentPetMasks.filter(
                (m) => (m.role ?? "other") === role,
              );
              if (!group.length) return null;

              const roleLabel =
                role === "reference"
                  ? "Reference Regions"
                  : role === "interest"
                  ? "Interest Regions"
                  : "Other Masks";
              const tracerLabel = petTracerLabel(niftiActiveModality!);

              return (
                <Fragment key={role}>
                  <tr className="shadow-inner bg-stone-500 font-bold">
                    <td className="py-2 text-center"></td>
                    <td className="py-2 text-left text-lg">
                      <span className="border-b-transparent decoration-transparent inline-block first-letter:uppercase">
                        {tracerLabel} {roleLabel}
                      </span>
                    </td>
                    <td colSpan={4} className="py-2"></td>
                  </tr>

                  {group.map((mask) => {
                    const selected = segmentsPerSeries.some(
                      (s) =>
                        s.representationUID === petMaskId(mask.url) &&
                        s.selected,
                    );

                    const values = getPetValues(
                      mask.url,
                      observationComponents,
                    );

                    return (
                      <PetMaskRow
                        key={mask.url}
                        mask={mask}
                        selected={selected}
                        values={values}
                      />
                    );
                  })}
                </Fragment>
              );
            })}
        </tbody>
      </table>
    </>
  );
}
